import { Box, Typography } from '@mui/material';
import { GilLearningOutcomeScore } from '@protos/learning_management/lms.assessment.student.apis_pb';
import { GilColumn, GilReportTable } from './GilReportTable';
import { GIL, GIL_CELL, gilSize, gilTableText, gilText } from './gilTheme';
import { PercentBar, ScoreBar, SectionCard } from './ReportWidgets';

/** "BLOOM_LEVEL_UNDERSTAND" or "understand" -> "Understand". */
const formatBloomLevel = (value?: string) => {
  const word = value
    ?.replace(/^BLOOM_LEVEL_/i, '')
    .replace(/_/g, ' ')
    .trim()
    .toLowerCase();
  return word ? word.charAt(0).toUpperCase() + word.slice(1) : undefined;
};

/** 3.456 -> "3.5", 4 -> "4" */
const formatMarks = (value?: number) =>
  value === undefined ? '—' : `${Math.round(value * 10) / 10}`;

interface IProps {
  outcomes?: GilLearningOutcomeScore[];
  /** Adds the outcome's share of the marks - one student on one project only. */
  showMarks?: boolean;
  /** Adds the projects column, for reports across several projects. */
  showProjects?: boolean;
  title?: string;
  subtitle?: string;
}

/**
 * Inferred learning outcome performance. GIL projects are marked as a whole,
 * so each outcome's score is the project score scaled by the share of that
 * outcome's resources completed. Hidden when the projects carry no outcomes.
 */
export const LearningOutcomesCard = ({
  outcomes = [],
  showMarks = false,
  showProjects = false,
  title = 'Learning Outcomes',
  subtitle = 'How each learning outcome of the project is going',
}: IProps) => {
  if (outcomes.length === 0) {
    return null;
  }

  const columns: GilColumn<GilLearningOutcomeScore>[] = [
    {
      key: 'index',
      header: '#',
      render: (_, index) => (
        <Typography sx={GIL_CELL.index}>{index + 1}</Typography>
      ),
    },
    {
      key: 'outcome',
      header: 'Learning Outcome',
      minWidth: 280,
      render: (row) => {
        const bloomLevel = formatBloomLevel(row.bloomLevel);
        return (
          <Box sx={{ paddingY: gilSize(10, 6) }}>
            <Typography
              sx={{ ...gilTableText(14, 500, GIL.cellName), lineHeight: 1.4 }}
            >
              {row.learningOutcome}
            </Typography>
            {bloomLevel && <Typography sx={GIL_CELL.sub}>{bloomLevel}</Typography>}
          </Box>
        );
      },
    },
    ...(showProjects
      ? [
          {
            key: 'projects',
            header: 'Projects',
            align: 'center' as const,
            render: (row: GilLearningOutcomeScore) => `${row.projectCount}`,
          },
        ]
      : []),
    {
      key: 'resources',
      header: 'Resources',
      align: 'center',
      render: (row) => `${row.resourceCount}`,
    },
    {
      key: 'completion',
      header: 'Resources Completed',
      render: (row) => <PercentBar value={row.completionPercent} />,
    },
    {
      key: 'score',
      header: 'Inferred Score',
      render: (row) => <ScoreBar value={row.inferredScorePercent} />,
    },
    ...(showMarks
      ? [
          {
            key: 'marks',
            header: 'Marks',
            align: 'center' as const,
            render: (row: GilLearningOutcomeScore) =>
              row.allocatedMaxMarks
                ? `${formatMarks(row.inferredMarks)}/${formatMarks(
                    row.allocatedMaxMarks
                  )}`
                : '—',
          },
        ]
      : []),
  ];

  return (
    <SectionCard title={title} subtitle={subtitle}>
      <GilReportTable
        columns={columns}
        rows={outcomes}
        rowKey={(row) => row.learningOutcomeId}
      />
      <Typography sx={gilText(13, 400, GIL.muted)}>
        Projects are marked as a whole, so each outcome&apos;s score is inferred:
        the project score scaled by the share of that outcome&apos;s resources
        completed. A score shows once the project is graded.
      </Typography>
    </SectionCard>
  );
};

export default LearningOutcomesCard;
